import { Form } from 'antd5';
import queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { useUploadContext } from 'components/FileUploader';
import { Modal, useMessage } from 'components/ui';
import { ModalContent } from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/ModalContent';
import { STEPS } from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/ModalContent/common';
import { type ImportResult } from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/types';
import { useGetIngestRecordEventsLazyQuery } from 'types/atlas-graphql';

import { ErrorState } from './ErrorState';

const POLL_INTERVAL = 5000;

type MultiInspectionDamageIngestionProps = {
  isOpen: boolean;
  setModalOpen: (open: boolean) => void;
};

export const MultiInspectionDamageIngestion = ({
  isOpen,
  setModalOpen,
}: MultiInspectionDamageIngestionProps) => {
  const { t } = useTranslation();
  const message = useMessage();
  const [step, setStep] = useState<number>(STEPS.upload);
  const [importResult, setImportResult] = useState<ImportResult | undefined>();
  const initialRequestMade = useRef(false);
  const { setUploading } = useUploadContext();
  const [form] = Form.useForm();

  const { search, pathname } = useLocation();
  const queryParams = queryString.parse(search);
  const history = useHistory();

  const [getIngestRecordEvents, { data: ingestData, stopPolling, startPolling }] =
    useGetIngestRecordEventsLazyQuery({
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (!ingestData) {
      return;
    }
    const { getRecordEvents } = ingestData;

    if (getRecordEvents.status && ['Complete', 'Error'].includes(getRecordEvents.status)) {
      const recordEvent =
        getRecordEvents.events?.find(event =>
          ['etl-success', 'etl-failure'].includes(event?.dataType ?? '')
        )?.event ?? {};

      if (Object.keys(recordEvent).length > 0) {
        if (recordEvent.results && recordEvent.params.recordType === 'multi-inspection-damage') {
          const { createdDamageCount, duplicateDamageCount, invalidDamageCount } =
            recordEvent.results;
          if (createdDamageCount && !duplicateDamageCount && !invalidDamageCount) {
            message.success(
              t('inspection.createdDamages', {
                count: createdDamageCount,
              })
            );
            handleClose();
          } else if (isOpen) {
            const parts = [
              createdDamageCount && t('inspection.createdDamages', { count: createdDamageCount }),
              duplicateDamageCount &&
                t('inspection.duplicateCount', { count: duplicateDamageCount }),
              invalidDamageCount && t('inspection.invalidRowCount', { count: invalidDamageCount }),
            ].filter(Boolean);
            message.warning(
              parts.length
                ? `${parts.join(', ')}. ${t(
                    'inspection.checkNotificationsReport',
                    'Check notifications to view the full error report.'
                  )}`
                : t(
                    'inspection.processingCompletedWithIssues',
                    'Processing completed with issues. Check notifications to view the full error report.'
                  )
            );
          } else {
            setImportResult(recordEvent.results);
            setStep(STEPS.error);
            setModalOpen(true);
          }
        } else if (isOpen) {
          message.error(
            t(
              'inspection.unexpectedErrorPreviousUpload',
              'Unexpected error processing previous upload. Please try again or contact support.'
            )
          );
        } else {
          setImportResult({
            errorMessage: t(
              'inspection.unexpectedErrorProcessingRequest',
              'Unexpected error occurred while processing request, please try again or contact support.'
            ),
            ...recordEvent.results,
          });
          setStep(STEPS.error);
          setModalOpen(true);
        }
        if (stopPolling) {
          stopPolling();
        }
        setUploading(false);
        initialRequestMade.current = false;
      }
    }
    // We only want this to fire when ingestData changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestData]);

  useEffect(() => {
    if (queryParams.multiInspectionUploadRecordId && queryParams.type !== 'SUCCESS') {
      handleCheckStatus(queryParams.multiInspectionUploadRecordId as string);
    }
    // Only want to fire when recordId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.multiInspectionUploadRecordId]);

  const handleCheckStatus = (recordId: string) => {
    if (stopPolling && initialRequestMade.current === true) {
      stopPolling();
    }
    getIngestRecordEvents({
      variables: {
        input: {
          recordId: recordId,
          bundleId: recordId,
        },
      },
    });
    if (!initialRequestMade.current) {
      initialRequestMade.current = true;
      startPolling(POLL_INTERVAL);
    }
  };

  const handleClose = () => {
    if (stopPolling && initialRequestMade.current === true) {
      stopPolling();
    }
    initialRequestMade.current = false;
    form.resetFields();
    setStep(STEPS.upload);
    history.replace(pathname);
    setModalOpen(false);
  };

  const isPendingDamagesTableVisible = step === STEPS.confirmation;

  return (
    <Modal
      title={t('inspection.uploadDamages', 'Upload Damages')}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      width={isPendingDamagesTableVisible ? '75vw' : 1200}
      style={isPendingDamagesTableVisible ? { top: '12.5vh' } : undefined}
      styles={
        isPendingDamagesTableVisible
          ? {
              body: {
                height: 'calc(75vh - 56px)',
                overflow: 'hidden',
              },
              content: {
                height: '75vh',
              },
            }
          : undefined
      }
    >
      <ModalContent
        form={form}
        step={step}
        setStep={setStep}
        setModalOpen={setModalOpen}
        handleCheckStatus={handleCheckStatus}
        onReset={form.resetFields}
        importResult={importResult}
        multiInspection={true}
        ErrorStateComponent={ErrorState}
      />
    </Modal>
  );
};
